import React, { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import FileUpload from 'Components/FileUpload';
import Modal, { ModalBody, ModalFooter } from 'Components/Modal';
// Material UI
import {
    Typography,
    Grid
} from '@mui/material';
import { Alert } from '@mui/material';
// Components
import { Button, Form, Select } from 'Components';
import { useFileUpload } from 'Core/hooks';
import { NotificationManager } from 'Components/Notifications';

// Actions
import {
    importSubUsers,
    openImportSubUserModal,
    resetImportState,
    clearError,
    setFileKey,
    setDelimiter
} from './redux/slice';

const ImportSubUserModal = (props) => {
    // Intl
    const { messages } = useIntl();
    const delimiters = [
        { id: ",", name: messages['importSubUser_comma']}, 
        { id: ";", name: messages['importSubUser_semicolon']}
    ];
    
    // Redux state
    const lang = useSelector(state => state.commomRedux.language);
    const { 
        openImport, 
        loading, 
        importResponse, 
        error,
        delimiter: reduxDelimiter
    } = useSelector((state) => state.subUsersImportReducer);
    
    // Hooks
    const dispatch = useDispatch();
    const formRef = useRef();
    const filesUpload = useFileUpload([]);
    
    // Local state
    const [delimiter, setDelimiterLocal] = useState({
        id: '',
        name: messages['importSubUser_selectDelimiter']
    });
    const [disableButton, setDisableButton] = useState(false);
    const [errorMessage, setErrorMessage] = useState({
        message: '',
        type: ''
    });

    const handleClose = useCallback(() => {
        dispatch(openImportSubUserModal(false));
        dispatch(resetImportState());
        setErrorMessage({
            message: '',
            type: ''
        });
        setDisableButton(false);
        setDelimiterLocal({
            id: '',
            name: messages['importSubUser_selectDelimiter']
        });
    }, [dispatch, messages]);

    // Handle successful import
    useEffect(() => {
        if (importResponse && openImport) {
            setDisableButton(true);
            NotificationManager.success(
                messages['importSubUser_started'],
                messages['importSubUser_title'],
                3000,
                null,
                null,
                'success'
            );
            handleClose();
        }
    }, [importResponse, openImport, messages, handleClose]);

    // Handle import errors
    useEffect(() => {
        if (error) {
            setErrorMessage({
                message: error,
                type: 'error'
            });
            setDisableButton(false);
        }
    }, [error]);

    const handleDelimiterChange = useCallback((selectedDelimiter) => {
        setDelimiterLocal(selectedDelimiter);
        dispatch(setDelimiter(selectedDelimiter.id));
    }, [dispatch]);

    const handleImportSubmit = useCallback(() => {
        if (filesUpload.value.length < 1) {
            setErrorMessage({
                message: messages['importSubUser_error_required'],
                type: 'error'
            });
            return false;
        }

        if (!delimiter.id) {
            setErrorMessage({
                message: messages['importSubUser_error_delimiter'],
                type: 'error'
            });
            return false;
        }

        // Clear any existing errors
        dispatch(clearError());
        setErrorMessage({ message: '', type: '' });
        
        // Start import with selected delimiter
        dispatch(setFileKey(filesUpload.value[0].fileKey));
        dispatch(importSubUsers({ 
            fileKey: filesUpload.value[0].fileKey, 
            delimiter: delimiter.id 
        }));
    }, [filesUpload.value, delimiter.id, dispatch, messages]);

    const downloadTemplate = useCallback((e) => {
        e.preventDefault();
        
        // Use selected delimiter or default to comma
        const delimiterSelected = delimiter?.id || ',';
        
        // Create CSV template for subUsers using main subUser translation keys
        const csvContent = `${messages['subUser_id']}${delimiterSelected}${messages['subUser_firstName']}${delimiterSelected}${messages['subUser_lastName']}${delimiterSelected}${messages['subUser_username']}${delimiterSelected}${messages['subUser_email']}${delimiterSelected}${messages['subUser_phone']}${delimiterSelected}${messages['subUser_tags']}${delimiterSelected}${messages['subUser_role']}${delimiterSelected}${messages['subUser_expiryDate']}\n`;
        
        const blob = new Blob([csvContent], {
            type: 'text/csv;encoding:utf-8;charset=UTF-8',
        });
        
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.setAttribute('download', 'subusers_template.csv');
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    }, [delimiter, lang]);

    return (
        <Fragment>
            <Modal
                title={messages['importSubUser_title']}
                open={openImport}
                loading={loading}
                position="right"
                size="small"
                handleClose={handleClose}
            >
                <ModalBody>
                    <div style={{ padding: '18px 24px 24px 24px', height: 650 }}>
                        {errorMessage.message && (
                            <Alert 
                                severity={errorMessage.type} 
                                onClose={() => setErrorMessage({ message: '', type: '' })}
                            >
                                {(() => {
                                    // Translate backend error codes to user-friendly messages
                                    const errorCode = errorMessage.message.includes('_') 
                                        ? errorMessage.message.split('_')[0] 
                                        : null;
                                    
                                    return errorCode && messages[`users_import_error_${errorCode}`]
                                        ? messages[`users_import_error_${errorCode}`]
                                        : errorMessage.message;
                                })()}
                            </Alert>
                        )}
                        
                        <Form
                            ref={formRef}
                            onValidSubmit={handleImportSubmit}
                        >
                            <div style={{ padding: 24 }}>
                                <Grid container direction="row" justifyContent="space-between" alignItems="flex-start">
                                    <Grid item sm={12}>
                                        <div style={{ marginBottom: 16, backgroundColor: '#fff3cd', padding: 16, borderRadius: 4, fontWeight: 500 }}>
                                            {messages['importSubUser_instructions']}
                                        </div>
                                        <Button
                                            type="button"
                                            className="btn-blue"
                                            style={{ width: '100%', marginBottom: 16 }}
                                            onClick={downloadTemplate}
                                        >
                                            {messages['importSubUser_downloadTemplate']}
                                        </Button>
                                    </Grid>
                                    
                                    <Grid item sm={12}>
                                        <Select
                                            id="delimiter"
                                            label={messages['importSubUser_delimiter']}
                                            placeholder={messages['importSubUser_selectDelimiter']}
                                            valueField="id"
                                            textField="name"
                                            options={delimiters}
                                            value={delimiter}
                                            required
                                            onChange={handleDelimiterChange}
                                        />
                                    </Grid>
                                    
                                    <Grid item sm={12}>
                                        <FileUpload
                                            {...filesUpload}
                                            label={`${messages['importSubUser_file']} (CSV)`}
                                            placeholder={messages['importSubUser_chooseFile']}
                                            types=".csv"
                                            helpText={messages['importSubUser_support_message']}
                                            maxSize={5}
                                            disabled={filesUpload?.value?.length > 0 || !delimiter?.id}
                                            maxSizeMessage={`${messages['importSubUser_max']}: 5MB`}
                                        />
                                        <small className="form-text text-muted">
                                            {messages['importSubUser_maxSizeFile']}
                                        </small>
                                    </Grid>
                                </Grid>
                            </div>
                        </Form>
                    </div>
                </ModalBody>
                
                <ModalFooter style={{ bottom: 50, textAlign: 'right' }}>
                    <Button
                        onClick={handleClose}
                        disabled={disableButton}
                        className="btn-cancel"
                    >
                        {messages['importSubUser_cancel']}
                    </Button>

                    <Button
                        onClick={(e) => {
                            if (e) e.preventDefault();
                            formRef.current.submit();
                        }}
                        disabled={disableButton || filesUpload?.value?.length === 0 || !delimiter?.id}
                        className="btn-blue"
                    >
                        {messages['importSubUser_button']}
                    </Button>
                </ModalFooter>
            </Modal>
        </Fragment>
    );
};

export default ImportSubUserModal;